// Shared display helpers (chip colors + i18n labels) for category / confidence / status,
// reused by ReportCard, InvestigationHistoryList, and InvestigationDetailDialog so the
// color maps stay in one place. Labels come from the aiTroubleshooting.* namespace.
import { Chip } from '@mui/material';
import type { ChipProps } from '@mui/material';
import type { useTranslate } from 'react-admin';
import type {
  Confidence,
  InvestigationStatus,
  RefusedReason,
  RootCauseCategory,
} from './types';

type ChipColor = ChipProps['color'];
type Translate = ReturnType<typeof useTranslate>;

const CATEGORY_COLOR: Record<RootCauseCategory, ChipColor> = {
  software_bug: 'warning',
  firmware_issue: 'warning',
  hardware_fault: 'error',
  network: 'info',
  config: 'default',
  user_error: 'default',
  unknown: 'default',
};

const CONFIDENCE_COLOR: Record<Confidence, ChipColor> = {
  high: 'success',
  medium: 'warning',
  low: 'default',
};

const STATUS_COLOR: Record<InvestigationStatus, ChipColor> = {
  running: 'info',
  completed: 'success',
  failed: 'error',
  refused: 'default',
};

export const categoryColor = (c: RootCauseCategory): ChipColor => CATEGORY_COLOR[c] ?? 'default';
export const confidenceColor = (c: Confidence): ChipColor => CONFIDENCE_COLOR[c] ?? 'default';
export const statusColor = (s: InvestigationStatus): ChipColor => STATUS_COLOR[s] ?? 'default';

export const categoryLabel = (translate: Translate, c: RootCauseCategory): string =>
  translate(`aiTroubleshooting.category.${c}`);
export const confidenceLabel = (translate: Translate, c: Confidence): string =>
  translate(`aiTroubleshooting.confidence.${c}`);
export const statusLabel = (translate: Translate, s: InvestigationStatus): string =>
  translate(`aiTroubleshooting.status.${s}`);
export const refusedReasonLabel = (translate: Translate, r: RefusedReason): string =>
  translate(`aiTroubleshooting.refusedReason.${r}`);
export const anchorTypeLabel = (
  translate: Translate,
  a: string,
): string => translate(`aiTroubleshooting.anchorType.${a}`);

export const CategoryChip = ({
  translate,
  category,
}: {
  translate: Translate;
  category: RootCauseCategory;
}) => (
  <Chip
    size="small"
    color={categoryColor(category)}
    label={categoryLabel(translate, category)}
  />
);

export const ConfidenceChip = ({
  translate,
  confidence,
}: {
  translate: Translate;
  confidence: Confidence;
}) => (
  <Chip
    size="small"
    variant="outlined"
    color={confidenceColor(confidence)}
    label={confidenceLabel(translate, confidence)}
  />
);

export const StatusChip = ({
  translate,
  status,
}: {
  translate: Translate;
  status: InvestigationStatus;
}) => <Chip size="small" color={statusColor(status)} label={statusLabel(translate, status)} />;

// Format an ISO-8601 UTC timestamp for display. Locale string is acceptable here (pure display,
// not stored); the underlying value is UTC from the backend.
export const formatTimestamp = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
};

// Render a model { provider, name } pair as "provider/name", or "—" when both are null.
export const formatModel = (model: {
  provider: string | null;
  name: string | null;
}): string => {
  if (!model) return '—';
  const { provider, name } = model;
  if (!provider && !name) return '—';
  return `${provider ?? '—'}/${name ?? '—'}`;
};
